@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.enginetabular

import androidx.compose.runtime.*
import kotlinx.browser.document
import org.example.project.settings.UISettings
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.AppDropdownMenuMode
import org.example.project.utils.All
import org.example.project.utils.copyTextToClipboard
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag
import org.w3c.files.FileReader

object TabularToolbarMode { const val DEFAULT = "DEFAULT"; const val CREATOR = "CREATOR" }

object TabularTheme {
    val ACCENT = UISettings.ACCENT
    val TEXT_LIGHT = Color("#FFFFFF")
    const val ICON_FILTER_ACTIVE = UISettings.ICON_FILTER_ACCENT

    @Composable
    private fun TabularNavButton(
        label: String,
        icon: String,
        isDisabled: Boolean = false,
        isActive: Boolean = false,
        showDivider: Boolean = true,
        onClick: () -> Unit
    ) {
        var hovered by remember { mutableStateOf(false) }

        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
            }
        }) {
            Button(attrs = {
                classes(TableStyles.topBarButton)
                if (isDisabled) {
                    attr("disabled", "true")
                    style { opacity(0.4); cursor("not-allowed") }
                }
                onMouseEnter { hovered = true }
                onMouseLeave { hovered = false }
                onClick { if (!isDisabled) onClick() }
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                }
            }) {
                Img(src = icon, attrs = {
                    classes(TableStyles.topBarIcon)
                    style {
                        property(
                            "filter",
                            if (hovered || isActive) ICON_FILTER_ACTIVE else "brightness(1)"
                        )
                    }
                })
                Span({
                    style {
                        if (hovered || isActive) color(ACCENT)
                        whiteSpace("nowrap")
                    }
                }) {
                    Text(label)
                }
            }
            if (showDivider) {
                Div({ classes(TableStyles.quickDivider) })
            }
        }
    }

    @Composable
    fun TabularLayout(
        title: String? = null,
        allHeaders: List<String>,
        headerDisplayLabels: Map<String, String> = emptyMap(),
        visibleColumns: List<String>,
        renderedData: List<Pair<String, List<String>>>,
        filteredDataSize: Int,
        headerNameToIndex: Map<String, Int>,
        nonEditableColIndices: Set<Int>,
        toggleColIndices: Set<Int>,
        dropdownOptionIndices: Map<Int, List<String>>,
        rowDropdownOptions: ((rowId: String, row: List<String>, colIndex: Int) -> List<String>?)? = null,
        calendarColumnIndices: Set<Int> = emptySet(),
    multilineColumnIndices: Set<Int> = emptySet(),
        cellBackgroundColors: Map<Pair<String, Int>, CellColor>,
        editable: Boolean,
        cellEditable: (rowId: String, column: String, value: String) -> Boolean = { _, _, _ -> true },
        selectionMode: String = TabularSelectionMode.ROWS,
        createButtonLabel: String = "Erstellen",
        snapButtonLabel: String = "Snap hinzufügen",
        saveButtonLabel: String = "Speichern",
        importButtonLabel: String = "Import",
        exportButtonLabel: String = "Export",
        exportFileName: String? = null,
        csvImportAccept: String = ".csv",
        toolbarMode: String = TabularToolbarMode.DEFAULT,
        generateButtonLabel: String = "",
        remainingResultsCount: Int,
        loadedDataCount: Int,
        totalDataCount: Int,
        selectedRows: Set<String>,
        selectedRowIndices: Set<Int> = emptySet(),
        selectedCell: Pair<String, Int>? = null,
        selectedCellRange: TabularCellRange? = null,
        filterTexts: List<MutableState<String>>,
        filterToggleHeaders: Set<String> = emptySet(),
        filterProfiles: Map<String, Map<String, String>> = emptyMap(),
        selectedFilterProfile: String? = null,
        onFilterProfileSelect: (String?) -> Unit = {},
        columnVisibility: Map<String, Boolean>,
        columnOrder: List<String>,
        visibleRowsCount: Int,
        showColumnToggle: Boolean,
        hoveredColIndex: Int?,
        onHeaderClick: ((Int) -> Unit)? = null,
        onHeaderLabelEdit: ((Int, String) -> Unit)? = null,
        // Callbacks - null = kein Button, Callback vorhanden = Button wird gerendert
        onCreate: (() -> Unit)?,
        onSnapAction: (() -> Unit)? = null,
        onDeleteMultiple: ((List<String>) -> Unit)?,
        isDeleteEnabled: (Set<String>) -> Boolean = { it.isNotEmpty() },
        onApproveSelected: ((Set<String>) -> Unit)?,
        onInstallSelected: ((Set<String>) -> Unit)?,
        isInstallEnabled: (Set<String>) -> Boolean = { it.isNotEmpty() },
        onCancelRows: ((Set<String>) -> Unit)?,
        isCancelEnabled: (Set<String>) -> Boolean = { it.isNotEmpty() },
        onCellEdit: ((String, Int, String) -> Unit)?,
        onCellSelectionChange: ((TabularCellRange) -> Unit)? = null,
        onSaveAll: (() -> Unit)?,
        onRowClick: ((String) -> Unit)?,
        onRowDoubleClick: ((String) -> Unit)? = null,
        onCellClick: ((String, Int) -> Unit)?,
        onCsvImport: ((String) -> Unit)?,
        allowImportButton: Boolean = true,
        allowExportButton: Boolean = true,
        allowSettingsButton: Boolean = true,
        allowSmartModeButton: Boolean = true,
        allowRefreshButton: Boolean = true,
        allowSearchButton: Boolean = true,
        showToolbarExtras: Boolean = true,
        showFilterRow: Boolean = true,
        showFillerRows: Boolean = true,
        smartMode: Boolean = false,
        dataColumnWidthCss: String? = null,
        verticalScroll: Boolean = true,
        onResetPassword: ((Set<String>) -> Unit)?,
        onGenerate: (() -> Unit)?,
        approveButtonLabel: String = "Kontrolliert",
        installButtonLabel: String = "Installieren",
        cancelButtonLabel: String = "Stornieren",
        cancelButtonIcon: String = "images/close.svg",
        extraNavButtons: List<TabularToolbarButton> = emptyList(),
        // interne UI-Callbacks
        onFilterClear: () -> Unit,
        onRefreshExecute: () -> Unit,
        onSearchExecute: () -> Unit,
        onColumnToggleToggle: () -> Unit,
        onColumnToggleClose: () -> Unit,
        onColumnToggleVisibilityChange: (header: String, isChecked: Boolean) -> Unit,
        onLoadMore: () -> Unit,
        onLimitChange: (newLimit: Int) -> Unit,
        onRowSelect: (id: String, index: Int, isShiftKey: Boolean, isCtrlKey: Boolean) -> Unit,
        onRowIndexSelect: ((Int, Boolean, Boolean) -> Unit)? = null,
        onRowIndexContextMenu: ((Int, Double, Double) -> Unit)? = null,
        onColumnOrderChange: (draggedHeader: String, targetHeader: String) -> Unit,
        onHoverColumnChange: (Int?) -> Unit,
        sortColumn: String? = null,
        sortAscending: Boolean = true,
        onSortChange: ((String) -> Unit)? = null,
        extraButtons: (@Composable () -> Unit)? = null,
        onCellContextMenu: ((String, Int, Double, Double) -> Unit)? = null,
        csvImportProfiles: Map<String, Map<String, String>> = emptyMap(),
        selectedCsvImportProfile: String? = null,
        onCsvImportProfileSelect: (String?) -> Unit = {},
        onCsvImportProfileSave: (String, Map<String, String>) -> Unit = { _, _ -> },
        onCsvImportProfileDelete: (String) -> Unit = {},
        csvExportProfiles: Map<String, Map<String, String>> = emptyMap(),
        selectedCsvExportProfile: String? = null,
        onCsvExportProfileSelect: (String?) -> Unit = {},
        onCsvExportProfileSave: (String, Map<String, String>) -> Unit = { _, _ -> },
        onCsvExportProfileDelete: (String) -> Unit = {},
        onFilterProfileSave: (String, Map<String, String>) -> Unit = { _, _ -> },
        onFilterProfileDelete: (String) -> Unit = {}
    ) {
        Style(TableStyles)

        val step = 1000

        var showSettings by remember { mutableStateOf(false) }
        var showFilterProfileMenu by remember { mutableStateOf(false) }
        var activeSmartMode by remember { mutableStateOf(smartMode) }
        LaunchedEffect(smartMode) {
            activeSmartMode = smartMode
        }

        val tabularButtons: @Composable () -> Unit = {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                }
            }) {
                @Composable
                fun renderExportButton() {
                    if (!allowExportButton) return
                    TabularNavButton(exportButtonLabel, "images/download.svg") {
                        val activeProfileMap = selectedCsvExportProfile?.let { csvExportProfiles[it] }.orEmpty()
                        val exportHeaders = visibleColumns.map { internal ->
                            activeProfileMap[internal]?.takeIf { it.isNotBlank() }
                                ?: headerDisplayLabels[internal]?.takeIf { it.isNotBlank() } ?: internal
                        }
                        val csv = buildString {
                            appendLine(toSemicolonCsvLine(exportHeaders))
                            renderedData.forEach { (_, row) ->
                                 val exportedRow = visibleColumns.map { header ->
                                     val index = headerNameToIndex[header]
                                     val value = if (index != null && index < row.size) row[index] else ""
                                     tabularCsvExportValue(value, index != null && index in toggleColIndices)
                                 }
                                appendLine(toSemicolonCsvLine(exportedRow))
                            }
                        }
                        triggerCsvDownload(exportFileName ?: "${title ?: "export"}.csv", csv)
                    }
                }

                @Composable
                fun renderImportButton() {
                    if (allowImportButton && onCsvImport != null) {
                        TabularNavButton(importButtonLabel, "images/upload.svg") {
                            createCsvImportInput(csvImportAccept) { csv -> onCsvImport(csv) }
                        }
                    }
                }

                @Composable
                fun renderSaveButton() {
                    if (onSaveAll != null) {
                        TabularNavButton(saveButtonLabel, "images/save.svg") { onSaveAll() }
                    }
                }

                @Composable
                fun renderSmartModeButton() {
                    if (!allowSmartModeButton) return
                    TabularNavButton(
                        label = if (activeSmartMode) "Smart" else "Normal",
                        icon = "images/tabular.svg",
                        isActive = activeSmartMode
                    ) {
                        activeSmartMode = !activeSmartMode
                    }
                }

                @Composable
                fun renderDeleteButton() {
                    if (onDeleteMultiple != null) {
                        TabularNavButton("Löschen", "images/delete.svg", !isDeleteEnabled(selectedRows)) {
                            onDeleteMultiple(selectedRows.toList())
                            onRowSelect("", -1, false, false)
                        }
                    }
                }

                @Composable
                fun renderRemainingButtons() {
                    if (!showToolbarExtras) return

                    if (showFilterRow && filterProfiles.isNotEmpty()) {
                        Div({
                            style {
                                position(Position.Relative)
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                            }
                        }) {
                            Button(attrs = {
                                classes(TableStyles.topBarButton)
                                style {
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    gap(8.px)
                                }
                                onClick { showFilterProfileMenu = !showFilterProfileMenu }
                            }) {
                                Img(src = "images/filter_list_off.svg", attrs = {
                                    classes(TableStyles.topBarIcon)
                                    style { property("filter", "brightness(1)") }
                                })
                                Span({ style { whiteSpace("nowrap") } }) {
                                    Text(selectedFilterProfile ?: "Filter")
                                }
                            }
                            Div({ classes(TableStyles.quickDivider) })

                            if (showFilterProfileMenu) {
                                Div({
                                    style {
                                        position(Position.Absolute)
                                        top(38.px)
                                        left(0.px)
                                        property("z-index", "50")
                                        minWidth(180.px)
                                        padding(6.px)
                                        backgroundColor(Color("#0f1218"))
                                        border(1.px, LineStyle.Solid, Color("#303645"))
                                        borderRadius(8.px)
                                        display(DisplayStyle.Flex)
                                        flexDirection(FlexDirection.Column)
                                        gap(4.px)
                                    }
                                }) {
                                    Div({
                                        style {
                                            padding(6.px, 8.px)
                                            borderRadius(6.px)
                                            color(Color.white)
                                            cursor("pointer")
                                            if (selectedFilterProfile.isNullOrBlank()) {
                                                backgroundColor(Color("rgba(255,255,255,0.08)"))
                                            }
                                        }
                                        onClick {
                                            onFilterProfileSelect(null)
                                            showFilterProfileMenu = false
                                        }
                                    }) { Text("Ohne Profil") }

                                    filterProfiles.keys.sortedBy { it.lowercase() }.forEach { profileName ->
                                        Div({
                                            style {
                                                padding(6.px, 8.px)
                                                borderRadius(6.px)
                                                color(Color.white)
                                                cursor("pointer")
                                                if (selectedFilterProfile == profileName) {
                                                    backgroundColor(Color("rgba(255,255,255,0.08)"))
                                                }
                                            }
                                            onClick {
                                                onFilterProfileSelect(profileName)
                                                showFilterProfileMenu = false
                                            }
                                        }) { Text(profileName) }
                                    }
                                }
                            }
                        }
                    }

                    if (onResetPassword != null) {
                        TabularNavButton("Passwort", "images/lock_reset.svg", selectedRows.isEmpty()) {
                            onResetPassword(selectedRows)
                            onRowSelect("", -1, false, false)
                        }
                    }

                    if (onGenerate != null) {
                        TabularNavButton(generateButtonLabel, "images/play_arrow.svg") { onGenerate() }
                    }

                    if (onApproveSelected != null) {
                        TabularNavButton(
                            approveButtonLabel,
                            "images/check_circle.svg",
                            selectedRows.isEmpty()
                        ) {
                            onApproveSelected(selectedRows)
                            onRowSelect("", -1, false, false)
                        }
                    }

                    if (onInstallSelected != null) {
                        TabularNavButton(
                            installButtonLabel,
                            "images/install.svg",
                            !isInstallEnabled(selectedRows)
                        ) {
                            onInstallSelected(selectedRows)
                            onRowSelect("", -1, false, false)
                        }
                    }

                    if (onCancelRows != null) {
                        TabularNavButton(cancelButtonLabel, cancelButtonIcon, !isCancelEnabled(selectedRows)) {
                            onCancelRows(selectedRows)
                            onRowSelect("", -1, false, false)
                        }
                    }

                    if (allowSettingsButton) {
                        TabularNavButton("Einstellungen", "images/settings.svg", showDivider = false) {
                            showSettings = !showSettings
                        }
                    }

                    if (remainingResultsCount > 0) {
                        TabularNavButton(
                            "Mehr (+${remainingResultsCount.coerceAtMost(step)})",
                            "images/more_horiz.svg"
                        ) { onLoadMore() }
                    }

                    run {
                        Span({
                            classes(TableStyles.topBarCounter)
                            onClick {
                                org.example.project.dashboard.AppDialogs.prompt("Neues Limit:", visibleRowsCount.toString()) { input ->
                                    input?.toIntOrNull()?.let { onLimitChange(it) }
                                }
                            }
                        }) {
                            Img(src = "images/tag.svg", attrs = { classes(TableStyles.topBarCounterIcon) })
                            Text("$loadedDataCount von $totalDataCount Datens\u00e4tze geladen")
                        }
                    }
                }

                @Composable
                fun renderExtraNavButtons() {
                    extraNavButtons.forEachIndexed { index, button ->
                        if (button.onDropdownChange != null) {
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                }
                            }) {
                                Div({
                                    classes(TableStyles.topBarButton)
                                    style {
                                        minWidth(210.px)
                                        if (!button.enabled) opacity(0.4)
                                    }
                                }) {
                                    Img(src = button.icon, attrs = {
                                        classes(TableStyles.topBarIcon)
                                        style { property("filter", "brightness(0) invert(1)") }
                                    })
                                    Div({ style { width(170.px) } }) {
                                        AppDropdown(
                                            options = button.dropdownOptions.map { (key, label) -> All(key = key, label = label) },
                                            selectedValue = button.selectedDropdownValue,
                                            onChange = button.onDropdownChange,
                                            placeholder = button.dropdownPlaceholder,
                                            enabled = button.enabled,
                                            appearance = AppDropdownAppearance.Underline,
                                            menuMode = AppDropdownMenuMode.Overlay
                                        )
                                    }
                                }
                                if (index < extraNavButtons.lastIndex) {
                                    Div({ classes(TableStyles.quickDivider) })
                                }
                            }
                        } else {
                            TabularNavButton(
                                label = button.label,
                                icon = button.icon,
                                isDisabled = !button.enabled,
                                showDivider = index < extraNavButtons.lastIndex
                            ) {
                                button.onClick()
                            }
                        }
                    }
                }

                when (toolbarMode) {
                    TabularToolbarMode.CREATOR -> {
                        if (onCreate != null) {
                            TabularNavButton(createButtonLabel, "images/add_circle.svg") { onCreate() }
                        }
                        if (onSnapAction != null) {
                            TabularNavButton(snapButtonLabel, "images/joinnetwork.svg") { onSnapAction() }
                        }
                        if (allowRefreshButton) {
                            TabularNavButton("Refresh", "images/refresh.svg") { onRefreshExecute() }
                        }
                        if (allowSearchButton) {
                            TabularNavButton("Suchen", "images/suche.svg") { onSearchExecute() }
                        }
                        if (showFilterRow) {
                            TabularNavButton("Filter löschen", "images/filter_list_off.svg") { onFilterClear() }
                        }
                        renderImportButton()
                        renderExportButton()
                        renderSmartModeButton()
                        renderSaveButton()
                        renderDeleteButton()
                        renderRemainingButtons()
                        renderExtraNavButtons()
                        if (extraButtons != null) {
                            extraButtons()
                        }
                    }

                    TabularToolbarMode.DEFAULT -> {
                        if (showFilterRow) {
                            TabularNavButton("Filter löschen", "images/filter_list_off.svg") { onFilterClear() }
                        }
                        if (allowSearchButton) {
                            TabularNavButton("Suchen", "images/suche.svg") { onSearchExecute() }
                        }
                        if (allowRefreshButton) {
                            TabularNavButton("Refresh", "images/refresh.svg") { onRefreshExecute() }
                        }
                        if (onCreate != null) {
                            TabularNavButton(createButtonLabel, "images/add_circle.svg") { onCreate() }
                        }
                        if (onSnapAction != null) {
                            TabularNavButton(snapButtonLabel, "images/joinnetwork.svg") { onSnapAction() }
                        }
                        renderDeleteButton()
                        renderRemainingButtons()
                        renderSaveButton()
                        renderSmartModeButton()
                        renderExportButton()
                        renderImportButton()
                        renderExtraNavButtons()
                        if (extraButtons != null) {
                            extraButtons()
                        }
                    }
                }
            }
        }

        if (showSettings) {
            CsvSettingsPopup(
                allHeaders = allHeaders,
                headerDisplayLabels = headerDisplayLabels,
                columnOrder = columnOrder,
                columnVisibility = columnVisibility,
                onColumnToggleVisibilityChange = onColumnToggleVisibilityChange,
                csvImportProfiles = csvImportProfiles,
                selectedCsvImportProfile = selectedCsvImportProfile,
                onCsvImportProfileSelect = onCsvImportProfileSelect,
                onCsvImportProfileSave = onCsvImportProfileSave,
                onCsvImportProfileDelete = onCsvImportProfileDelete,
                csvExportProfiles = csvExportProfiles,
                selectedCsvExportProfile = selectedCsvExportProfile,
                onCsvExportProfileSelect = onCsvExportProfileSelect,
                onCsvExportProfileSave = onCsvExportProfileSave,
                onCsvExportProfileDelete = onCsvExportProfileDelete,
                filterToggleHeaders = filterToggleHeaders,
                filterProfiles = filterProfiles,
                selectedFilterProfile = selectedFilterProfile,
                onFilterProfileSelect = onFilterProfileSelect,
                onFilterProfileSave = onFilterProfileSave,
                onFilterProfileDelete = onFilterProfileDelete,
                onClose = { showSettings = false }
            )
        }

        // Root Wrapper
        Div({
            classes(TableStyles.tableWrapper)
            if (!verticalScroll) {
                style {
                    property("height", "auto")
                    overflow("visible")
                }
            }
        }) {

            // Topbar / Buttons direkt im Tabular rendern
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                    padding(8.px)
                    minWidth(0.px); maxWidth(100.percent); flexShrink(0); boxSizing("border-box")
                    overflowX("auto"); overflowY("hidden")
                }
            }) {
                tabularButtons()
            }

            Div({
                classes(TableStyles.tableScrollFrame)
                if (activeSmartMode) {
                    classes(TableStyles.smartScrollFrame)
                }
                if (!verticalScroll) {
                    style {
                        flex("none")
                        overflowY("visible")
                    }
                }
            }) {
                TabularTable(
                    visibleColumns,
                    headerDisplayLabels,
                    renderedData,
                    headerNameToIndex,
                    nonEditableColIndices,
                    toggleColIndices,
                    dropdownOptionIndices,
                    rowDropdownOptions,
                    calendarColumnIndices,
                    multilineColumnIndices,
                    cellBackgroundColors,
                    editable,
                    selectionMode,
                    selectedRows,
                    selectedRowIndices,
                    selectedCell,
                    selectedCellRange,
                    filterTexts,
                    hoveredColIndex,
                    onColumnOrderChange,
                    onHoverColumnChange,
                    onHeaderClick,
                    onHeaderLabelEdit,
                    onRowSelect,
                    onRowClick,
                    onRowDoubleClick,
                    onRowIndexSelect,
                    onRowIndexContextMenu,
                    onCellClick,
                    onCellSelectionChange,
                    onCellContextMenu = { rowId, colIndex, clientX, clientY ->
                        if (onCellContextMenu != null) {
                            onCellContextMenu(rowId, colIndex, clientX, clientY)
                        } else {
                            val value = renderedData.firstOrNull { it.first == rowId }
                                ?.second?.getOrNull(colIndex).orEmpty()
                            copyTextToClipboard(value)
                        }
                    },
                    onCellEdit,
                    cellEditable = cellEditable,
                    sortColumn = sortColumn,
                    sortAscending = sortAscending,
                    onSortChange = onSortChange,
                    showFillerRows = showFillerRows && !activeSmartMode,
                    showFilterRow = showFilterRow,
                    dataColumnWidthCss = dataColumnWidthCss,
                    smartMode = activeSmartMode
                )
            }
        }
    }
}

private fun triggerCsvDownload(fileName: String, csvContent: String) {
    val blob = Blob(arrayOf("\uFEFF$csvContent"), BlobPropertyBag("text/csv;charset=utf-8"))
    val url = URL.createObjectURL(blob)

    val link = document.createElement("a") as HTMLAnchorElement
    link.href = url
    link.download = fileName

    document.body?.appendChild(link)
    link.click()
    document.body?.removeChild(link)
}

private fun createCsvImportInput(accept: String, onFileRead: (String) -> Unit) {
    val input = document.createElement("input") as HTMLInputElement
    input.type = "file"
    input.accept = accept
    input.onchange = {
        val file = input.files?.item(0)
        if (file != null) {
            val reader = FileReader()
            reader.onload = {
                val text = reader.result as String
                onFileRead(text)
            }
            reader.readAsText(file)
        }
    }
    input.click()
}
